import { useEffect } from "react";
import { Button } from "@mantine/core";
import { useGo } from "../components/ConsoleShell";
import {
  DataTable, EmptyState, GhostButton, Prompt, Screen, StatusDot, type Column,
  LoadError,
}  from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { compact, dimsLabel } from "../lib/vf";
import type { Namespace } from "../types/domain";

const COLUMNS: Column[] = [
  { key: "namespace", label: "namespace", width: "2.4fr" },
  { key: "type", label: "type", width: "1.2fr" },
  { key: "vectors", label: "vectors", width: "0.9fr" },
  { key: "dims", label: "dims", width: "0.7fr" },
  { key: "p95", label: "p95", width: "0.7fr" },
  { key: "status", label: "status", width: "0.9fr" },
];

export default function Namespaces() {
  const go = useGo();
  const openDetail = useUiStore((s) => s.openDetail);
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const loadError = useConsoleStore((s) => s.errors.namespaces);
  const load = useConsoleStore((s) => s.loadNamespaces);

  useEffect(() => {
    if (workspaceId) void load();
  }, [workspaceId, load]);

  return (
    <Screen>
      <Prompt
        command="vf namespaces ls"
        action={<GhostButton onClick={() => go("/namespaces/new")}>+ create</GhostButton>}
      />
      <LoadError message={loadError} />

      {namespaces.length === 0 ? (
        <EmptyState
          title="no namespaces yet"
          body="A namespace is one isolated index — its own dimensions, embedder profile and BM25 setting. Create the first one to start upserting documents."
          action={
            <Button size="md" fz="13.5px" fw={600} onClick={() => go("/namespaces/new")}>
              ▸ create your first namespace
            </Button>
          }
        />
      ) : (
        <DataTable<Namespace>
          columns={COLUMNS}
          rows={namespaces}
          rowKey={(n) => n.id}
          onRowClick={(n) => openDetail("namespace", n.id)}
          renderCell={(n, key) => {
            switch (key) {
              case "namespace":
                return <span style={{ color: "var(--vf-text)" }}>{n.label}</span>;
              case "type":
                return <span style={{ color: "var(--vf-sec)" }}>{n.kind}</span>;
              case "vectors":
                return <span style={{ color: "var(--vf-acc)" }}>{compact(n.vectors)}</span>;
              case "dims":
                return <span style={{ color: "var(--vf-sec)" }}>{dimsLabel(n.dims)}</span>;
              case "p95":
                return <span style={{ color: "var(--vf-sec)" }}>{n.p95}ms</span>;
              case "status":
                return <StatusDot status={n.status} />;
              default:
                return null;
            }
          }}
          note={`${namespaces.length} namespaces · click a row for config, stats & actions`}
        />
      )}
    </Screen>
  );
}
